// MeetVideoRoom — renders the in-call UI tree (header, tile grid, captions,
// control bar). LiveKit Room construction + connect/disconnect lifecycle
// lives at the page level in pages/meet.js (DOMQL v3.14 doesn't fire onCreate
// reliably on `if:`/`extends:` children, so we can't do lifecycle here).
//
// The live Room instance is stashed on `root._livekitRoom` by the page, and
// participants are mirrored to `root.livekitParticipants`. This component
// just renders from that state.

export const MeetVideoRoom = {
  padding: '0',
  flow: 'y',
  flex: '1 1 0',
  minHeight: '0',
  position: 'relative',

  Header: {
    flow: 'x',
    align: 'center space-between',
    padding: 'A B1',
    borderBottomWidth: '1px',
    borderBottomColor: 'line',
    borderBottomStyle: 'solid',

    Title: {
      tag: 'h2',
      fontSize: 'A1',
      fontWeight: '600',
      margin: '0',
      color: 'title',
      text: (el, s) => {
        const room = (s.root?.rooms || []).find(
          (r) => r.id === s.root?.activeRoomId
        )
        return room?.name || 'Call'
      }
    },

    TranscribeBadge: {
      hide: (el, s) => {
        const room = (s.root?.rooms || []).find(
          (r) => r.id === s.root?.activeRoomId
        )
        return !room?.transcribeEnabled
      },
      tag: 'span',
      padding: 'V Z',
      round: 'Y',
      background: 'cyan',
      color: 'white',
      fontSize: 'Y1',
      fontWeight: '600',
      letterSpacing: '.05em',
      textTransform: 'uppercase',
      text: '● Transcribing'
    }
  },

  // Waiting-room strip — pinned below the header. Owner-only; hidden when
  // no pending guest requests.
  MeetWaitingRoomBanner: {},

  // Reactive grid of tiles. Layout adapts via getRoomMode based on the
  // participant count — for the MVP we just use a flex wrap and let the
  // tiles size themselves.
  TileGrid: {
    flex: '1 1 0',
    minHeight: '0',
    overflow: 'auto',
    padding: 'Z1',
    display: 'grid',
    gap: 'A',
    gridTemplateColumns: (el, s) => {
      const n = (s.root?.livekitParticipants || []).length || 1
      if (n <= 1) return '1fr'
      if (n <= 4) return 'repeat(2, 1fr)'
      if (n <= 9) return 'repeat(3, 1fr)'
      return 'repeat(4, 1fr)'
    },

    childExtends: 'MeetParticipantTile',
    childrenAs: 'state',
    children: (el, s) => s.root?.livekitParticipants || []
  },

  // Shown when the active room is ended (host ended it remotely while you're
  // still in the call view, or you navigated back into an ended room).
  EndedOverlay: {
    hide: (el, s) => {
      const room = (s.root?.rooms || []).find(
        (r) => r.id === s.root?.activeRoomId
      )
      return !room?.endedAt
    },
    position: 'absolute',
    inset: '0',
    align: 'center center',
    background: 'black.7',
    backdropFilter: 'blur(8px)',
    flow: 'y',
    gap: 'Z1',
    color: 'white',

    Icon: { icon: 'recordstop', boxSize: 'C' },
    Text: {
      tag: 'p',
      margin: '0',
      fontSize: 'A1',
      fontWeight: '600',
      text: 'This room has ended'
    },
    Sub: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'white.6',
      text: 'The call has been closed by the host.'
    },
    Actions: {
      flow: 'x',
      gap: 'Z1',
      marginTop: 'Z',

      TranscriptBtn: {
        hide: (el, s) => {
          const id = s.root?.activeRoomId
          if (!id) return true
          const room = (s.root?.rooms || []).find((r) => r.id === id)
          return !room?.transcribeEnabled
        },
        tag: 'button',
        text: 'View transcript',
        padding: 'Z B',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'white.3',
        borderStyle: 'solid',
        background: 'transparent',
        color: 'white',
        cursor: 'pointer',
        fontSize: 'Z2',
        onClick: (e, el, s) => {
          const id = s.root?.activeRoomId
          if (!id) return
          s.root?.update({ meetView: 'transcript' })
          if (typeof window !== 'undefined') {
            history.replaceState(null, '', `#room=${id}&view=transcript`)
          }
        }
      },
      BackBtn: {
        tag: 'button',
        text: 'Back to calls',
        padding: 'Z B',
        round: 'Z',
        border: 'none',
        theme: 'primary',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        onClick: (e, el, s) => {
          s.root?.update({ meetView: 'list', activeRoomId: null })
          if (typeof window !== 'undefined') history.replaceState(null, '', '')
        }
      }
    }
  },

  ErrorOverlay: {
    hide: (el, s) => !s.root?.meetCallError,
    position: 'absolute',
    inset: '0',
    align: 'center center',
    background: 'black.6',
    backdropFilter: 'blur(8px)',
    flow: 'y',
    gap: 'Z1',
    color: 'white',

    Text: {
      tag: 'p',
      margin: '0',
      fontSize: 'A',
      text: (el, s) => `Couldn't join: ${s.root?.meetCallError}`
    },
    BackBtn: {
      tag: 'button',
      text: 'Back to calls',
      padding: 'Z B',
      round: 'Z',
      border: 'none',
      theme: 'primary',
      cursor: 'pointer',
      onClick: (e, el, s) => {
        s.root?.update({
          meetCallError: null,
          meetView: 'list',
          activeRoomId: null
        })
      }
    }
  },

  MeetLiveCaptions: {},
  MeetControlBar: {}
}
